import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  CookieStorage,
} from "amazon-cognito-identity-js";

function getCookieStorage() {
  const hostname = window.location.hostname;
  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
  const data = {
    secure: !isLocalhost,
    sameSite: "lax" as const,
  };
  return new CookieStorage(data);
}

// App client used for the Google SSO (Hosted UI, OAuth Authorization Code +
// PKCE) flow. This is the only authentication path the app supports, so all
// sessions and tokens in the app are issued by this app client.
export function getOAuthUserPool() {
  const data = {
    UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
    ClientId: import.meta.env.VITE_COGNITO_OAUTH_CLIENT_ID,
    Storage: getCookieStorage(),
  };
  return new CognitoUserPool(data);
}

export function getCurrentUser() {
  return getOAuthUserPool().getCurrentUser();
}

export function signOut() {
  getOAuthUserPool().getCurrentUser()?.signOut();
}

// Persists the tokens returned by Cognito's /oauth2/token endpoint (after
// completing the Google federated login through the Hosted UI) using the
// same CognitoUserSession/CookieStorage mechanism the rest of the app relies
// on via getCurrentUser()/getUserToken().
export function persistOAuthSession(tokens: {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}) {
  const idToken = new CognitoIdToken({ IdToken: tokens.idToken });
  const accessToken = new CognitoAccessToken({
    AccessToken: tokens.accessToken,
  });
  const refreshToken = new CognitoRefreshToken({
    RefreshToken: tokens.refreshToken,
  });
  const session = new CognitoUserSession({
    IdToken: idToken,
    AccessToken: accessToken,
    RefreshToken: refreshToken,
  });

  const payload = idToken.decodePayload();
  const username: string = payload["cognito:username"] || payload.sub;

  const user = new CognitoUser({
    Username: username,
    Pool: getOAuthUserPool(),
    Storage: getCookieStorage(),
  });
  user.setSignInUserSession(session);
  return user;
}

export const getUserToken = () => {
  const user = getCurrentUser();
  if (user === null) {
    return Promise.resolve("");
  }
  return new Promise((resolve, reject) => {
    user.getSession((err: Error, result: CognitoUserSession | null) => {
      if (err !== null || result === undefined || result === null) {
        if (
          err?.name === "NotAuthorizedException" ||
          err?.message?.includes("Refresh Token has expired") ||
          err?.message?.includes("Invalid Refresh Token")
        ) {
          user.signOut();
        }
        return reject(err);
      }
      return resolve(result.getAccessToken().getJwtToken());
    });
  });
};
